import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Clock, Calendar, ArrowLeft } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useProducts } from '../context/ProductsContext';
import { blogPosts, type BlogPost } from '@shared/blog';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import { buildBreadcrumbJsonLd } from '../lib/seoSchema';
import NotFoundPage from './NotFoundPage';

/**
 * Turn internal site paths inside blog paragraph text into real <Link>
 * elements. Matches /store, /faq, /about, /contact, /blog/<slug>,
 * /brand/<slug>, /product/<slug>. Anything else stays as text.
 *
 * This is what gives the in-content CTAs ("browse our full nicotine
 * pouch collection at /store") an actual <a> tag for crawler internal
 * linking and user navigation.
 */
const INTERNAL_PATH_RE = /(\/(?:store|faq|about|contact|blog\/[a-z0-9-]+|brand\/[a-z0-9-]+|product\/[a-z0-9-]+))/g;
function renderParagraph(text: string): React.ReactNode[] {
  const parts = text.split(INTERNAL_PATH_RE);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <Link key={i} to={part} className="text-emerald-500 underline-offset-2 hover:underline">
        {part}
      </Link>
    ) : (
      part
    )
  );
}

const CATEGORY_ACCENT: Record<string, string> = {
  Beginner: '#10b981',
  Brand: '#3b82f6',
  Comparison: '#8b5cf6',
  Lifestyle: '#f59e0b',
  Health: '#ef4444',
  India: '#06b6d4',
};

function PrevNextBlogNav({ prev, next, isDark }: { prev?: BlogPost; next?: BlogPost; isDark: boolean }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-16">
      {prev ? (
        <Link
          to={`/blog/${prev.slug}`}
          className={`group rounded-xl border p-4 transition-all hover:border-emerald-500 ${
            isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
          }`}
        >
          <div className={`flex items-center gap-1 text-xs font-bold uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'} group-hover:text-emerald-500`}>
            <ChevronLeft size={14} /> Previous Blog
          </div>
          <p className={`text-sm font-semibold line-clamp-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {prev.title}
          </p>
        </Link>
      ) : (
        <div />
      )}
      {next ? (
        <Link
          to={`/blog/${next.slug}`}
          className={`group rounded-xl border p-4 transition-all hover:border-emerald-500 sm:text-right ${
            isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
          }`}
        >
          <div className={`flex items-center gap-1 text-xs font-bold uppercase tracking-widest mb-2 sm:justify-end ${isDark ? 'text-gray-500' : 'text-gray-400'} group-hover:text-emerald-500`}>
            Next Blog <ChevronRight size={14} />
          </div>
          <p className={`text-sm font-semibold line-clamp-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {next.title}
          </p>
        </Link>
      ) : (
        <div />
      )}
    </div>
  );
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const { isDark } = useTheme();
  const { products } = useProducts();

  const postIndex = blogPosts.findIndex((p) => p.slug === slug);
  const post = postIndex >= 0 ? blogPosts[postIndex] : undefined;
  const prev = postIndex > 0 ? blogPosts[postIndex - 1] : undefined;
  const next = postIndex >= 0 && postIndex < blogPosts.length - 1 ? blogPosts[postIndex + 1] : undefined;

  // "You May Also Like": same UI as ProductDetailPage. Selection: relatedBrand → relatedCategory → bestSellers
  const related = useMemo(() => {
    if (!post) return [];
    if (post.relatedBrand) {
      return products.filter((p) => p.brand === post.relatedBrand && p.inStock).slice(0, 8);
    }
    if (post.relatedCategory) {
      return products.filter((p) => p.category === post.relatedCategory && p.inStock).slice(0, 8);
    }
    return products.filter((p) => p.bestSeller && p.inStock).slice(0, 8);
  }, [post, products]);

  if (!post) return <NotFoundPage />;

  const accent = CATEGORY_ACCENT[post.category] ?? '#10b981';
  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting', 
    headline: post.title,
    description: post.metaDescription,
    image: `https://zerosmoke.in${post.image}`,
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    author: { '@type': 'Organization', name: 'ZeroSmoke' },
    publisher: {
      '@type': 'Organization',
      name: 'ZeroSmoke',
      logo: { '@type': 'ImageObject', url: 'https://zerosmoke.in/favicon.svg' },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://zerosmoke.in/blog/${post.slug}` },
    keywords: post.keywords.join(', '),
  };

  const breadcrumbJsonLd = buildBreadcrumbJsonLd([
    { name: 'Home', url: '/' },
    { name: 'Blog', url: '/blog' },
    { name: post.title, url: `/blog/${post.slug}` },
  ]);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      <SEO
        title={`${post.title} — ZeroSmoke`}
        description={post.metaDescription}
        canonical={`/blog/${post.slug}`}
        ogType="article"
        ogImage={`https://zerosmoke.in${post.image}`}
        jsonLd={[articleJsonLd, breadcrumbJsonLd]}
      />

      <Breadcrumb items={[
        { name: 'Home', href: '/' },
        { name: 'Blog', href: '/blog' },
        { name: post.title },
      ]} />

      {/* Back link */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Link
          to="/blog"
          className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-emerald-500 ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          <ArrowLeft size={16} /> Back to Blog
        </Link>
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Category chip + meta */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex flex-wrap items-center gap-3 mb-5 text-xs"
        >
          <span
            className="px-2.5 py-1 font-bold rounded-md uppercase tracking-wider text-white"
            style={{ backgroundColor: accent }}
          >
            {post.category}
          </span>
          <span className={`flex items-center gap-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            <Calendar size={12} /> {new Date(post.publishedAt).toLocaleDateString('en-IN', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </span>
          <span className={`flex items-center gap-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            <Clock size={12} /> {post.readTime} min read
          </span>
        </motion.div>

        {/* Title (H1) */}
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
          className={`text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-8 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}
        >
          {post.title}
        </motion.h1>

        {/* Hero image / placeholder container */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="aspect-[16/9] rounded-2xl overflow-hidden mb-10 relative"
          style={{ background: `linear-gradient(145deg, ${accent}15, ${accent}30, ${accent}15)` }}
        >
          <img
            src={post.image}
            alt={`${post.title} — ZeroSmoke blog post hero image`}
            className="w-full h-full object-cover"
            loading="eager"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </motion.div>

        {/* Intro paragraph */}
        <p className={`text-lg leading-relaxed mb-10 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          {renderParagraph(post.intro)}
        </p>

        {/* Sections */}
        <div className="space-y-10">
          {post.sections.map((section, idx) => (
            <section key={idx}>
              <h2
                className={`text-2xl sm:text-3xl font-extrabold tracking-tight mb-4 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}
              >
                {section.heading}
              </h2>
              <div className="space-y-4">
                {section.paragraphs.map((p, pi) => (
                  <p
                    key={pi}
                    className={`text-base leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    {renderParagraph(p)}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Previous / Next blog navigation */}
        <PrevNextBlogNav prev={prev} next={next} isDark={isDark} />
      </article>

      {/* You May Also Like — direct port of ProductDetailPage block */}
      {related.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20">
          <div className="mt-20">
            <h2 className={`text-2xl font-extrabold mb-8 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              You May Also Like
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
